/**
 * How the header merges an AI reading (understand.v1, T-151) into what the
 * query parser understood. The reference implementation of the client rules in
 * docs/search/understand.md; Sales and Pocket port it and run the same golden
 * file (docs/search/understand-merge-golden.json), so keep it a literal
 * transcription of those rules.
 *
 * The parser stays authoritative: it wins every filter key it filled, and the
 * AI only fills the gaps. The words left for the text search are the ones
 * neither reading used.
 */
import type { ParsedQuery } from './parseQuery';
import {
  CHIP_DATE_FIELDS, SEARCH_ENTITIES, dateFieldChoices,
  type ChipDate, type ChipDateField, type ChipFilters, type SearchEntity,
} from './searchFilterChips';

/** The understand.v1 response body (docs/search/understand.md). */
export interface UnderstandV1 {
  schema_version: string;
  source: 'ai' | 'parser';
  confidence: number | null;
  q: string;
  filters: {
    entity?: string;
    company_ids?: string[];
    date?: { field: string | null; from: string; to: string; tz?: string };
    models?: string[];
    status?: string[];
  } | null;
  labels: { companies: Record<string, string> };
}

export interface MergedQuery {
  /** What's left for the text search. */
  q: string;
  filters: ChipFilters;
  /** Chip keys the AI filled (entity:x, company:id, model:CODE, status:s, date:from/to). */
  aiKeys: string[];
  /** The AI's names for the companies it filled, by id. */
  labels: Record<string, string>;
}

// A word's identity for matching, as the server matches the residual: case,
// edge punctuation and a possessive 's don't count ("Five's" matches "five").
const edge = (s: string) => s.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
const wordKey = (w: string) => edge(edge(w.toLowerCase()).replace(/['’]s$/u, ''));
const words = (s: string) => s.trim().split(/\s+/).filter(Boolean);

/** The parser's residual words that the AI's residual also has, as a multiset, in the parser's order and case. */
function intersectResiduals(parserQ: string, aiQ: string): string {
  const left = new Map<string, number>();
  for (const w of words(aiQ)) left.set(wordKey(w), (left.get(wordKey(w)) ?? 0) + 1);
  const kept: string[] = [];
  for (const w of words(parserQ)) {
    const n = left.get(wordKey(w)) ?? 0;
    if (n > 0) { kept.push(w); left.set(wordKey(w), n - 1); }
  }
  return kept.join(' ');
}

const isEntity = (e: unknown): e is SearchEntity => typeof e === 'string' && (SEARCH_ENTITIES as readonly string[]).includes(e);
const isField = (f: unknown): f is ChipDateField => typeof f === 'string' && (CHIP_DATE_FIELDS as readonly string[]).includes(f);
const nonEmpty = <T>(xs: T[] | undefined): xs is T[] => Array.isArray(xs) && xs.length > 0;

/**
 * Never called under user chip edits: the header applies no reading while the
 * user has edits, and an edit drops the reading (useUniversalSearch), so the
 * user's choices are never layered on an AI value that can come and go.
 */
export function mergeUnderstanding(parsed: ParsedQuery, ai: UnderstandV1 | null): MergedQuery {
  const unchanged: MergedQuery = { q: parsed.q, filters: parsed.filters, aiKeys: [], labels: {} };
  const f = ai && ai.schema_version === 'understand.v1' && ai.source === 'ai' ? ai.filters : null;
  if (!f) return unchanged;
  // A newer vocabulary than this client knows: ignore the whole reading.
  if (f.entity !== undefined && !isEntity(f.entity)) return unchanged;
  if (f.date && f.date.field !== null && !isField(f.date.field)) return unchanged;

  const p = parsed.filters;
  const out: ChipFilters = {};
  const aiKeys: string[] = [];
  const labels: Record<string, string> = {};

  const aiDate = f.date;
  const entity = p.entity ?? (isEntity(f.entity) ? f.entity : undefined);
  if (entity) {
    out.entity = entity;
    if (!p.entity) aiKeys.push(`entity:${entity}`);
  }

  if (nonEmpty(p.company_ids)) out.company_ids = p.company_ids;
  else if (nonEmpty(f.company_ids)) {
    out.company_ids = f.company_ids;
    for (const id of f.company_ids) {
      aiKeys.push(`company:${id}`);
      const name = ai!.labels?.companies?.[id];
      if (typeof name === 'string') labels[id] = name;
    }
  }

  if (nonEmpty(p.model_codes)) out.model_codes = p.model_codes;
  else if (nonEmpty(f.models)) {
    out.model_codes = f.models;
    for (const m of f.models) aiKeys.push(`model:${m}`);
  }

  if (nonEmpty(p.status)) out.status = p.status;
  else if (nonEmpty(f.status)) {
    out.status = f.status;
    for (const s of f.status) aiKeys.push(`status:${s}`);
  }

  // Dates: an implicit parser date (R6: nothing was typed) counts as none.
  const choices = dateFieldChoices(entity);
  const aiField = aiDate && isField(aiDate.field) && choices.includes(aiDate.field) ? aiDate.field : null;
  let refined = false;
  if (p.date && !(p.date.implicit && aiDate)) {
    if (aiField && aiField !== p.date.field) {
      out.date = { ...p.date, field: aiField };
      refined = true;
    } else {
      out.date = p.date;
    }
  } else if (aiDate) {
    const date: ChipDate = { field: aiField, from: aiDate.from, to: aiDate.to };
    out.date = date;
    aiKeys.push(`date:${date.from}/${date.to}`);
  }

  if (!aiKeys.length && !refined) return unchanged;
  return { q: intersectResiduals(parsed.q, ai!.q ?? ''), filters: out, aiKeys, labels };
}
